import { Box, Heading, Stack, Tag, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { RequestDraftRoute, RequestDraftStatus } from '@prisma/client';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { MultiSwitch } from '../components/MultiSwitch';
import { RequestDraftDetail } from '../components/RequestDrafts/RequestDraftDetail';
import { trpc } from '../config/trpc';

type DraftRow = {
  id: number;
  created_at: Date;
  route: RequestDraftRoute;
  email_from: string;
  email_subject: string;
  account?: { name: string } | null;
  is_low_confidence: boolean;
  payload?: { origin_airports?: string[]; destination_airports?: string[] } | null;
  request?: {
    origin_airports?: { airport_as_origin_id: string | null }[];
    destination_airports?: { airport_as_destination_id: string | null }[];
  } | null;
};

const joinAirports = (ids?: (string | null)[]) => ids?.filter(Boolean).join('/') || undefined;

type RouteLabel = { label: string; colorScheme: string };

const UNLABELLED_ROUTE: RouteLabel = { label: 'Other', colorScheme: 'gray' };

/**
 * Only the routes the classifier still produces. `RequestDraftRoute` keeps the retired ops-lifecycle values — Postgres
 * cannot drop an enum value in place, and old rows reference them — but the taxonomy collapsed to
 * `booking_confirmation` + `other`, so labelling the rest was describing a system that no longer exists.
 */
const ROUTE_LABELS: Partial<Record<RequestDraftRoute, RouteLabel>> = {
  [RequestDraftRoute.BookingConfirmation]: { label: 'Booking confirmation', colorScheme: 'green' },
  [RequestDraftRoute.Other]: UNLABELLED_ROUTE,
};

const labelFor = (route: RequestDraftRoute): RouteLabel => ROUTE_LABELS[route] ?? UNLABELLED_ROUTE;

// The inbox shows exactly one route. Booking confirmations are the workflow being built; every other route is recorded
// in the ledger for measurement but has no review view, so surfacing it here would only be noise.
const INBOX_ROUTE = RequestDraftRoute.BookingConfirmation;

const laneFor = (row: DraftRow) => {
  const origin =
    joinAirports(row.payload?.origin_airports) ??
    joinAirports(row.request?.origin_airports?.map((a) => a.airport_as_origin_id));
  const destination =
    joinAirports(row.payload?.destination_airports) ??
    joinAirports(row.request?.destination_airports?.map((a) => a.airport_as_destination_id));
  if (!origin && !destination) return null;
  return `${origin ?? '?'} → ${destination ?? '?'}`;
};

const DraftListItem = ({ row, isSelected, onSelect }: { row: DraftRow; isSelected: boolean; onSelect: () => void }) => {
  const lane = laneFor(row);

  return (
    <Box
      as="button"
      textAlign="left"
      w="full"
      px="4"
      py="3"
      borderBottomWidth="1px"
      bg={isSelected ? 'blackAlpha.100' : undefined}
      borderLeftWidth="3px"
      borderLeftColor={isSelected ? 'primary.500' : 'transparent'}
      _hover={{ bg: isSelected ? 'blackAlpha.100' : 'blackAlpha.50' }}
      onClick={onSelect}
    >
      <Stack spacing="1">
        <Stack direction="row" align="center" spacing="2">
          <Show if={!!lane}>
            <Text fontSize="xs" fontWeight="bold" color="gray.600">
              {lane}
            </Text>
          </Show>
          <Box flex="1" />
          <Text fontSize="xs" color="gray.500">
            {format(new Date(row.created_at), 'MMM d, HH:mm')}
          </Text>
        </Stack>
        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
          {row.email_subject}
        </Text>
        <Stack direction="row" align="center" spacing="2">
          <Text fontSize="xs" color="gray.600" noOfLines={1} flex="1">
            {row.email_from}
          </Text>
          <Show if={!!row.account?.name}>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {row.account?.name}
            </Text>
          </Show>
          <Show if={!row.account?.name}>
            <Tag size="sm" colorScheme="orange">
              Unassigned
            </Tag>
          </Show>
          {/* Confidence only surfaces when it is NEWS: the model's self-reported
              number saturates at 0.95 on everything unambiguous, so printing it
              on every row is noise. The exceptional state is the tag. */}
          <Show if={row.is_low_confidence}>
            <Tag size="sm" colorScheme="orange">
              Low confidence
            </Tag>
          </Show>
        </Stack>
      </Stack>
    </Box>
  );
};

export const AiInboxScreen = () => {
  const [status, setStatus] = useState<RequestDraftStatus>(RequestDraftStatus.Pending);
  const [searchParams] = useSearchParams();
  const deepLinkedDraftId = Number(searchParams.get('draft')) || null;
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(deepLinkedDraftId);

  const { data, refetch } = trpc.getRequestDrafts.useQuery({ status, route: INBOX_ROUTE, skip: 0, take: 200 });
  const { data: counts, refetch: refetchCounts } = trpc.getRequestDraftCounts.useQuery({ route: INBOX_ROUTE });

  const statusOptions = useMemo(
    () =>
      Object.values(RequestDraftStatus).map((value) => ({
        value,
        label: counts ? `${value} (${counts[value]})` : value,
      })),
    [counts],
  );

  const rows = useMemo(() => ((data as DraftRow[]) ?? []).filter((row) => row.route === INBOX_ROUTE), [data]);

  useEffect(() => {
    if (selectedDraftId !== null && selectedDraftId === deepLinkedDraftId) return;
    if (rows.length === 0) {
      setSelectedDraftId(null);
      return;
    }
    if (selectedDraftId === null || !rows.some((row) => row.id === selectedDraftId)) {
      setSelectedDraftId(rows[0].id);
    }
  }, [rows, selectedDraftId, deepLinkedDraftId]);

  const handleResolved = useCallback(async () => {
    const index = rows.findIndex((row) => row.id === selectedDraftId);
    const next = rows[index + 1] ?? rows[index - 1] ?? null;
    setSelectedDraftId(next?.id ?? null);
    await Promise.all([refetch(), refetchCounts()]);
  }, [rows, selectedDraftId, refetch, refetchCounts]);

  return (
    <Stack spacing="0" h="full">
      <Stack p="4" spacing="4">
        <Stack direction="row" align="center" w="full" spacing="4">
          <Heading variant="h3">AI Inbox</Heading>
          <Tag colorScheme={labelFor(INBOX_ROUTE).colorScheme}>{labelFor(INBOX_ROUTE).label}</Tag>
          <MultiSwitch
            options={statusOptions}
            defaultValue={status}
            onChange={(value) => setStatus(value as RequestDraftStatus)}
          />
        </Stack>
      </Stack>

      <Stack direction={{ base: 'column', lg: 'row' }} flex="1" spacing="0" overflow="hidden" borderTopWidth="1px">
        {/* List */}
        <Box
          w={{ base: 'full', lg: 'sm' }}
          maxH={{ base: '80', lg: 'unset' }}
          flexShrink="0"
          overflowY="auto"
          borderRightWidth={{ base: 0, lg: '1px' }}
          borderBottomWidth={{ base: '1px', lg: 0 }}
        >
          <Show if={rows.length === 0}>
            <Text p="4" fontSize="sm" color="gray.500">
              No {status.toLowerCase()} booking confirmations.
            </Text>
          </Show>
          {rows.map((row) => (
            <DraftListItem
              key={row.id}
              row={row}
              isSelected={row.id === selectedDraftId}
              onSelect={() => setSelectedDraftId(row.id)}
            />
          ))}
        </Box>

        {/* Review pane */}
        <Box flex="1" minW="0" overflow="hidden">
          <RequestDraftDetail key={selectedDraftId} draftId={selectedDraftId} onResolved={handleResolved} />
        </Box>
      </Stack>
    </Stack>
  );
};
